import { helpers } from "@acadia-ui/theme";
import { useState, useEffect, useMemo, useRef, useCallback, useReducer } from "react";

export default function useData({ fetchItems, DataStore, parentCampaignUniqueID, auditLevel, flip }) {
	const dataTableApi = useRef(null);
	const abortController = useRef(null);
	const disabledCellStateRefresh = useRef(null);
	const [data, setData] = useState([]);
	const [config, setConfig] = useState();

	const [renderedData, setRenderedData] = useReducer((prevData, newData) => {
		const updatedData = newData || prevData || [];
		dataTableApi?.current?.dataProvider?.update?.(updatedData);
		clearTimeout(disabledCellStateRefresh.current);
		disabledCellStateRefresh.current = setTimeout(() => {
			dataTableApi?.current?.refreshDisabledCellStates?.();
		}, 100);
		return updatedData;
	}, []);

	// const [renderedData, setRenderedData] = useState([]);

	const fetch = useCallback(async () => {
		abortController.current?.abort?.();

		const currentAbortController = new AbortController();
		abortController.current = currentAbortController;

		try {
			const result = await fetchItems(
				{
					platform: { value: auditLevel.PlatformID },
					auditlevel: { value: auditLevel.AuditLevelID }
				},
				parentCampaignUniqueID,
				{ abortController: currentAbortController }
			);

			if (currentAbortController.signal.aborted || abortController.current !== currentAbortController || result?.aborted) {
				return { aborted: true };
			}

			return result;
		} catch (error) {
			if (currentAbortController.signal.aborted || error?.name === "AbortError") {
				return { aborted: true };
			}

			throw error;
		}
	}, [fetchItems, auditLevel, parentCampaignUniqueID]);

	useEffect(() => {
		abortController.current?.abort?.();
		setData(null);
		setConfig(null);
	}, [parentCampaignUniqueID]);

	useEffect(() => {
		return () => {
			abortController.current?.abort?.();
			clearTimeout(disabledCellStateRefresh.current);
		};
	}, []);

	const refresh = useCallback(
		async (force = false) => {
			if (parentCampaignUniqueID && auditLevel) {
				let aborted = false;

				try {
					const result = await fetch();

					if (result?.aborted) {
						aborted = true;
						return result;
					}

					setData(DataStore.getData());
					setConfig(DataStore.getConfig());
					setRenderedData(DataStore.getData());

					return { aborted: false };
				} catch (error) {
					setData(null);
					setConfig(null);
					setRenderedData(null);

					return { aborted: false, error };
				} finally {
					if (force && !aborted) {
						flip();
					}
				}
			}

			return { aborted: false, skipped: true };
		},
		[parentCampaignUniqueID, auditLevel, fetch, DataStore, flip]
	);

	helpers.useAsyncEffect(async () => {
		await refresh();
	}, [refresh]);

	return useMemo(() => {
		return { data, config, dataTableApi, refresh, setData, setConfig, renderedData, setRenderedData, fetch };
	}, [data, config, dataTableApi, refresh, setData, setConfig, renderedData, setRenderedData, fetch]);
}
